/**
 * Client Analytics — Original Overview Page
 * APX Hub — Financial health, anomalies, and audit trail for all CORE clients.
 * Matches the original screenshot:
 *   - 5-card KPI strip: Total Clients | With Data | Healthy | Review | Alert
 *   - Table columns: CLIENT | PRACTICE | INCOME | EXPENSES | NET PROFIT | ANOMALIES | HEALTH | LAST PULLED | Actions
 *   - Filters: Search + All Practices + All Health
 *   - "Pull All Clients" button (managers only)
 *   - Per-row re-pull icon + "View →" action
 */
import { useState, useMemo } from "react";
import DashboardLayout from "@/components/DashboardLayout";
import { trpc, trpcClient } from "@/lib/trpc";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import {
  RefreshCw,
  Search,
  TrendingUp,
  TrendingDown,
  Loader2,
  BarChart3,
  RotateCcw,
  ArrowRight,
  CheckCircle,
  AlertCircle,
  Clock,
  Download,
  Flag,
  X,
  ShieldAlert,
  CheckCheck,
  EyeOff,
} from "lucide-react";

// --- Flag Data Modal -----------------------------------------------------------
function FlagDataModal({
  open,
  onClose,
  clientId,
  clientName,
  period,
  income,
  expenses,
  netProfit,
  flaggedBy,
  flaggedByRole,
  onSuccess,
}: {
  open: boolean;
  onClose: () => void;
  clientId: number;
  clientName: string;
  period: string;
  income: number;
  expenses: number;
  netProfit: number;
  flaggedBy: string;
  flaggedByRole: string;
  onSuccess: () => void;
}) {
  const [field, setField] = useState<"income" | "expenses" | "netProfit" | "other">("income");
  const [note, setNote] = useState("");
  const raiseMutation = trpc.dataFlags.raise.useMutation({
    onSuccess: () => {
      toast.success(`Flag raised for ${clientName} — an admin will investigate`);
      setNote("");
      onSuccess();
      onClose();
    },
    onError: (e) => toast.error(`Failed to raise flag: ${e.message}`),
  });
  if (!open) return null;
  const fieldValues: Record<string, number> = { income, expenses, netProfit };
  const fieldLabels: Record<string, string> = { income: "Income", expenses: "Expenses", netProfit: "Net Profit", other: "Other" };
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center"
      style={{ background: "rgba(0,0,0,0.45)" }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div
        className="rounded-2xl p-6 w-full max-w-md shadow-2xl"
        style={{ background: C.cardBg, border: `1px solid ${C.cardBorder}` }}
      >
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <ShieldAlert className="w-5 h-5" style={{ color: C.amber }} />
            <span className="font-bold text-base" style={{ color: C.textPrimary, fontFamily: "'Playfair Display', serif" }}>
              Flag Suspicious Data
            </span>
          </div>
          <button onClick={onClose} style={{ color: C.textFaint }}><X className="w-4 h-4" /></button>
        </div>
        <p className="text-sm mb-4" style={{ color: C.textMuted }}>
          <strong style={{ color: C.textPrimary }}>{clientName}</strong> &middot; {period}
        </p>
        <div className="mb-4">
          <p className="text-xs font-semibold uppercase tracking-wider mb-2" style={{ color: C.textMuted }}>Which figure looks wrong?</p>
          <div className="flex gap-2 flex-wrap">
            {(["income", "expenses", "netProfit", "other"] as const).map((f) => (
              <button
                key={f}
                onClick={() => setField(f)}
                className="px-3 py-1.5 rounded-lg text-sm font-semibold border transition-all"
                style={field === f
                  ? { background: C.amberBg, color: C.amber, borderColor: C.amberBorder }
                  : { background: "transparent", color: C.textMuted, borderColor: C.cardBorder }}
              >
                {fieldLabels[f]}
                {f !== "other" && fieldValues[f] !== undefined && (
                  <span className="ml-1 opacity-70 text-xs">
                    ({new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(fieldValues[f])})
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>
        <div className="mb-5">
          <p className="text-xs font-semibold uppercase tracking-wider mb-1.5" style={{ color: C.textMuted }}>Why does this look wrong?</p>
          <textarea
            className="w-full rounded-lg px-3 py-2 text-sm resize-none"
            style={{ background: C.tableHead, border: `1px solid ${C.cardBorder}`, color: C.textPrimary, minHeight: 80 }}
            placeholder="e.g. Income should be around $500K based on client invoices — $1 is clearly a data error"
            value={note}
            onChange={(e) => setNote(e.target.value)}
          />
        </div>
        <div className="flex gap-2 justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-lg text-sm font-semibold border"
            style={{ background: "transparent", color: C.textMuted, borderColor: C.cardBorder }}
          >Cancel</button>
          <button
            onClick={() => raiseMutation.mutate({
              coreClientId: clientId,
              clientName,
              period,
              field,
              flaggedValue: field !== "other" ? fieldValues[field] : undefined,
              note: note.trim(),
              flaggedBy,
              flaggedByRole,
            })}
            disabled={note.trim().length < 5 || raiseMutation.isPending}
            className="px-4 py-2 rounded-lg text-sm font-semibold flex items-center gap-1.5"
            style={{ background: C.amber, color: "#fff", opacity: note.trim().length < 5 ? 0.5 : 1 }}
          >
            {raiseMutation.isPending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Flag className="w-3.5 h-3.5" />}
            Raise Flag
          </button>
        </div>
      </div>
    </div>
  );
}

// --- Flag Review Panel (admin) -------------------------------------------------
function FlagReviewPanel({
  open,
  onClose,
  period,
  resolvedBy,
}: {
  open: boolean;
  onClose: () => void;
  period: string;
  resolvedBy: string;
}) {
  const { data: flags = [], refetch } = trpc.dataFlags.list.useQuery({ status: "all", period }, { enabled: open });
  const updateMutation = trpc.dataFlags.update.useMutation({
    onSuccess: () => { toast.success("Flag updated"); refetch(); },
    onError: (e) => toast.error(`Update failed: ${e.message}`),
  });
  const [noteMap, setNoteMap] = useState<Record<number, string>>({});
  if (!open) return null;
  const openFlags = flags.filter(f => f.status === "open" || f.status === "investigating");
  const closedFlags = flags.filter(f => f.status === "resolved" || f.status === "dismissed");
  const fieldColor = (f: string) => f === "income" ? C.green : f === "expenses" ? C.red : C.amber;
  const fmt = (n: number | null) => n != null ? new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(n) : "—";
  return (
    <div
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center"
      style={{ background: "rgba(0,0,0,0.45)" }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div
        className="rounded-2xl p-6 w-full max-w-2xl max-h-[85vh] overflow-y-auto shadow-2xl"
        style={{ background: C.cardBg, border: `1px solid ${C.cardBorder}` }}
      >
        <div className="flex items-center justify-between mb-5">
          <div className="flex items-center gap-2">
            <ShieldAlert className="w-5 h-5" style={{ color: C.red }} />
            <span className="font-bold text-lg" style={{ color: C.textPrimary, fontFamily: "'Playfair Display', serif" }}>
              Data Integrity Flags
            </span>
            {openFlags.length > 0 && (
              <span className="text-xs font-bold px-2 py-0.5 rounded-full" style={{ background: C.redBg, color: C.red, border: `1px solid ${C.redBorder}` }}>
                {openFlags.length} open
              </span>
            )}
          </div>
          <button onClick={onClose} style={{ color: C.textFaint }}><X className="w-4 h-4" /></button>
        </div>
        {openFlags.length === 0 && closedFlags.length === 0 && (
          <p className="text-sm text-center py-8" style={{ color: C.textFaint }}>No flags raised for this period.</p>
        )}
        {openFlags.length > 0 && (
          <div className="mb-6">
            <p className="text-xs font-bold uppercase tracking-wider mb-3" style={{ color: C.textMuted }}>Open / Under Investigation</p>
            <div className="flex flex-col gap-3">
              {openFlags.map(flag => (
                <div key={flag.id} className="rounded-xl p-4" style={{ background: C.tableHead, border: `1px solid ${C.cardBorder}` }}>
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <div>
                      <span className="font-semibold text-sm" style={{ color: C.textPrimary }}>{flag.clientName}</span>
                      <span className="mx-1.5" style={{ color: C.textFaint }}>·</span>
                      <span className="text-xs font-bold uppercase" style={{ color: fieldColor(flag.field) }}>{flag.field}</span>
                      {flag.flaggedValue != null && (
                        <span className="ml-1.5 text-xs" style={{ color: C.textMuted }}>({fmt(flag.flaggedValue)})</span>
                      )}
                    </div>
                    <span
                      className="text-xs px-2 py-0.5 rounded-full font-semibold shrink-0"
                      style={flag.status === "investigating"
                        ? { background: C.blueBg, color: C.blue, border: `1px solid ${C.blueBorder}` }
                        : { background: C.amberBg, color: C.amber, border: `1px solid ${C.amberBorder}` }}
                    >{flag.status}</span>
                  </div>
                  <p className="text-xs mb-1" style={{ color: C.textMuted }}>
                    Flagged by <strong>{flag.flaggedBy}</strong> ({flag.flaggedByRole}) · {new Date(flag.createdAt).toLocaleDateString()}
                  </p>
                  <p className="text-sm mb-3" style={{ color: C.textSecond }}>{flag.note}</p>
                  <textarea
                    className="w-full rounded-lg px-3 py-2 text-xs resize-none mb-2"
                    style={{ background: C.cardBg, border: `1px solid ${C.cardBorder}`, color: C.textPrimary, minHeight: 56 }}
                    placeholder="Admin resolution note (optional)…"
                    value={noteMap[flag.id] ?? ""}
                    onChange={(e) => setNoteMap(m => ({ ...m, [flag.id]: e.target.value }))}
                  />
                  <div className="flex gap-2">
                    <button
                      onClick={() => updateMutation.mutate({ id: flag.id, status: "investigating", resolutionNote: noteMap[flag.id], resolvedBy })}
                      className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-semibold border"
                      style={{ background: C.blueBg, color: C.blue, borderColor: C.blueBorder }}
                    ><ShieldAlert className="w-3 h-3" /> Investigating</button>
                    <button
                      onClick={() => updateMutation.mutate({ id: flag.id, status: "resolved", resolutionNote: noteMap[flag.id], resolvedBy })}
                      className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-semibold border"
                      style={{ background: C.greenBg, color: C.green, borderColor: C.greenBorder }}
                    ><CheckCheck className="w-3 h-3" /> Resolved</button>
                    <button
                      onClick={() => updateMutation.mutate({ id: flag.id, status: "dismissed", resolutionNote: noteMap[flag.id], resolvedBy })}
                      className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-semibold border"
                      style={{ background: C.tableHead, color: C.textMuted, borderColor: C.cardBorder }}
                    ><EyeOff className="w-3 h-3" /> Dismiss</button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
        {closedFlags.length > 0 && (
          <div>
            <p className="text-xs font-bold uppercase tracking-wider mb-3" style={{ color: C.textFaint }}>Resolved / Dismissed</p>
            <div className="flex flex-col gap-2">
              {closedFlags.map(flag => (
                <div key={flag.id} className="rounded-xl px-4 py-3 flex items-start gap-3" style={{ background: C.tableHead, border: `1px solid ${C.cardBorder}`, opacity: 0.7 }}>
                  <CheckCircle className="w-4 h-4 mt-0.5 shrink-0" style={{ color: flag.status === "resolved" ? C.green : C.textFaint }} />
                  <div>
                    <span className="font-semibold text-sm" style={{ color: C.textPrimary }}>{flag.clientName}</span>
                    <span className="mx-1.5" style={{ color: C.textFaint }}>·</span>
                    <span className="text-xs uppercase" style={{ color: fieldColor(flag.field) }}>{flag.field}</span>
                    <p className="text-xs mt-0.5" style={{ color: C.textFaint }}>{flag.resolutionNote || "No resolution note"} · by {flag.resolvedBy}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// --- Design tokens (matches APX shell) ----------------------------------------
const C = {
  pageBg:       "#F8F6F2",
  headerBg:     "#EDE8E0",
  headerBorder: "#D8D2C8",
  cardBg:       "#FFFFFF",
  cardBorder:   "#D8D2C8",
  cardShadow:   "0 1px 4px rgba(10,20,40,0.06)",
  textPrimary:  "oklch(0.12 0.04 240)",
  textSecond:   "oklch(0.28 0.04 240)",
  textMuted:    "oklch(0.45 0.03 240)",
  textFaint:    "oklch(0.65 0.02 240)",
  gold:         "oklch(0.62 0.14 75)",
  goldLight:    "oklch(0.94 0.06 85)",
  goldBorder:   "oklch(0.80 0.10 75)",
  green:        "oklch(0.40 0.15 155)",
  greenBg:      "oklch(0.94 0.06 155)",
  greenBorder:  "oklch(0.75 0.12 155)",
  amber:        "oklch(0.52 0.14 75)",
  amberBg:      "oklch(0.96 0.06 85)",
  amberBorder:  "oklch(0.80 0.12 85)",
  red:          "oklch(0.50 0.20 25)",
  redBg:        "oklch(0.96 0.05 25)",
  redBorder:    "oklch(0.75 0.16 25)",
  blue:         "oklch(0.45 0.15 240)",
  blueBg:       "oklch(0.94 0.06 240)",
  blueBorder:   "oklch(0.72 0.12 240)",
  tableHead:    "#EDE8E0",
  tableRowHov:  "#F5F2EE",
  tableDiv:     "#E8E3DC",
};

// --- Helpers ------------------------------------------------------------------

function fmtCurrency(n: number): string {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  }).format(n);
}

function fmtDate(d: Date | string | null): string {
  if (!d) return "—";
  const date = typeof d === "string" ? new Date(d) : d;
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function practiceLabel(key: string): string {
  if (key === "apx")     return "APX";
  if (key === "apx-hrb") return "APX-HRB";
  if (key === "apx-bbn") return "APX-BBN";
  return key.toUpperCase();
}

function practiceBadgeStyle(key: string): React.CSSProperties {
  const base: React.CSSProperties = {
    display: "inline-flex",
    alignItems: "center",
    padding: "2px 8px",
    borderRadius: 6,
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: "0.03em",
  };
  if (key === "apx")
    return { ...base, background: C.goldLight, color: C.gold, border: `1px solid ${C.goldBorder}` };
  if (key === "apx-hrb")
    return { ...base, background: C.blueBg, color: C.blue, border: `1px solid ${C.blueBorder}` };
  if (key === "apx-bbn")
    return { ...base, background: C.greenBg, color: C.green, border: `1px solid ${C.greenBorder}` };
  return { ...base, background: C.tableHead, color: C.textMuted, border: `1px solid ${C.cardBorder}` };
}

function defaultPeriod(): string {
  // Default to prior month-end
  const now = new Date();
  const prior = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  return `${prior.getFullYear()}-${String(prior.getMonth() + 1).padStart(2, "0")}`;
}

// --- KPI Card -----------------------------------------------------------------

function KpiCard({
  label,
  value,
  color,
}: {
  label: string;
  value: number | string;
  color?: string;
}) {
  return (
    <div
      className="rounded-xl p-5 flex flex-col gap-1"
      style={{
        background: C.cardBg,
        border: `1px solid ${C.cardBorder}`,
        boxShadow: C.cardShadow,
      }}
    >
      <p className="text-xs font-semibold uppercase tracking-widest" style={{ color: C.textMuted }}>
        {label}
      </p>
      <p
        className="text-4xl font-bold leading-none mt-1"
        style={{ color: color ?? C.textPrimary, fontFamily: "'Playfair Display', serif" }}
      >
        {value}
      </p>
    </div>
  );
}

// --- Health Badge -------------------------------------------------------------

function HealthBadge({ health }: { health: string }) {
  if (health === "green") {
    return (
      <span
        className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold"
        style={{ background: C.greenBg, color: C.green, border: `1px solid ${C.greenBorder}` }}
      >
        <span className="w-1.5 h-1.5 rounded-full inline-block" style={{ background: C.green }} />
        Healthy
      </span>
    );
  }
  if (health === "yellow") {
    return (
      <span
        className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold"
        style={{ background: C.amberBg, color: C.amber, border: `1px solid ${C.amberBorder}` }}
      >
        <span className="w-1.5 h-1.5 rounded-full inline-block" style={{ background: C.amber }} />
        Review
      </span>
    );
  }
  if (health === "red") {
    return (
      <span
        className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold"
        style={{ background: C.redBg, color: C.red, border: `1px solid ${C.redBorder}` }}
      >
        <span className="w-1.5 h-1.5 rounded-full inline-block" style={{ background: C.red }} />
        Alert
      </span>
    );
  }
  // pending
  return (
    <span
      className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold"
      style={{ background: C.tableHead, color: C.textMuted, border: `1px solid ${C.cardBorder}` }}
    >
      <span className="w-1.5 h-1.5 rounded-full inline-block" style={{ background: C.textFaint }} />
      No Data
    </span>
  );
}

// --- Main Page ----------------------------------------------------------------

export default function ClientAnalyticsOverview() {
  const { user } = useAuth();

  const [period, setPeriod] = useState(defaultPeriod);
  const [search, setSearch] = useState("");
  const [practiceFilter, setPracticeFilter] = useState("all");
  const [healthFilter, setHealthFilter] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all");

  const { data: clientCounts } = trpc.xero.getActiveClientCount.useQuery();

  const {
    data: summaries = [],
    isLoading,
    refetch,
  } = trpc.analytics.getSummaries.useQuery({ period });

  const pullClientMutation = trpc.analytics.pullClient.useMutation({
    onSuccess: (res) => {
      toast.success(`${res.clientName}: data pulled (${res.anomaliesFound} anomalies)`);
      refetch();
    },
    onError: (e) => toast.error(`Pull failed: ${e.message}`),
  });

  const pullAllMutation = trpc.analytics.pullAll.useMutation({
    onSuccess: (res) => {
      toast.success(`Pulled ${res.success}/${res.total} clients`);
      refetch();
    },
    onError: (e) => toast.error(`Pull all failed: ${e.message}`),
  });

  const isManager = [
    "admin",
    "super_admin",
    "controller",
    "bookkeeping_manager",
    "bookkeeper_supervisor",
  ].includes(user?.role ?? "");

  const isAdmin = ["admin", "super_admin"].includes(user?.role ?? "");

  // ── Data integrity flags ─────────────────────────────────────────────────────
  const [flagModal, setFlagModal] = useState<null | {
    clientId: number; clientName: string;
    income: number; expenses: number; netProfit: number;
  }>(null);
  const [flagReviewOpen, setFlagReviewOpen] = useState(false);
  const { data: openFlagCount = 0, refetch: refetchFlagCount } = trpc.dataFlags.openCount.useQuery();
  // ── Reconciliation export ─────────────────────────────────────────────────────
  const [exportLoading, setExportLoading] = useState(false);

  async function handleExportReconciliation() {
    if (!user?.email) return;
    setExportLoading(true);
    try {
      const result = await trpcClient.analytics.exportClientReconciliation.query({
        callerEmail: user.email,
      });
      if (!result?.rows?.length) {
        toast.error("No data returned from reconciliation export.");
        return;
      }
      // Build CSV
      const headers = [
        "XERO_NAME",
        "HUB_NAME",
        "CORE_NAME",
        "PRACTICE",
        "XERO_TENANT_ID",
        "IN_XERO",
        "IS_ACTIVE",
        "EMAIL",
        "PHONE",
        "MISSING_CONTACT",
        "NOTE",
      ];
      const escape = (v: string) => `"${String(v ?? "").replace(/"/g, '""')}"`;
      const csvLines = [
        headers.join(","),
        ...result.rows.map((r) =>
          [
            escape(r.xeroName),
            escape(r.hubName),
            escape(r.coreName),
            escape(r.practiceKey),
            escape(r.xeroTenantId),
            r.hasXeroLink ? "YES" : "NO",
            r.isActive ? "YES" : "NO",
            escape(r.email),
            escape(r.phone),
            escape(r.missingContact),
            escape(r.note),
          ].join(",")
        ),
      ];
      const csv = csvLines.join("\n");
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      const date = new Date().toISOString().slice(0, 10);
      a.href = url;
      a.download = `APX_Client_Reconciliation_${date}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(
        `Downloaded ${result.rows.length} rows (${result.xeroOrgCount} Xero orgs, ${result.hubClientCount} Hub clients)`
      );
    } catch (err: any) {
      toast.error(`Export failed: ${err?.message ?? "Unknown error"}`);
    } finally {
      setExportLoading(false);
    }
  }

  // KPI stats
  const stats = useMemo(() => {
    const total    = summaries.length;
    const withData = summaries.filter((s) => s.health !== "pending").length;
    const healthy  = summaries.filter((s) => s.health === "green").length;
    const review   = summaries.filter((s) => s.health === "yellow").length;
    const alert    = summaries.filter((s) => s.health === "red").length;
    return { total, withData, healthy, review, alert };
  }, [summaries]);

  // Filtered rows
  const filtered = useMemo(() => {
    let rows = summaries;
    if (practiceFilter !== "all") rows = rows.filter((r) => r.practiceKey === practiceFilter);
    // typeFilter: 'xero' = only Xero-connected (have xeroTenantId), 'non_xero' = non-Xero clients
    // Note: summaries come from core_clients (Xero only), non-Xero clients have no summaries
    if (typeFilter === "xero") rows = rows; // summaries are all Xero
    if (typeFilter === "non_xero") rows = []; // non-Xero clients have no analytics data
    if (healthFilter !== "all") {
      if (healthFilter === "healthy") rows = rows.filter((r) => r.health === "green");
      else if (healthFilter === "review") rows = rows.filter((r) => r.health === "yellow");
      else if (healthFilter === "alert") rows = rows.filter((r) => r.health === "red");
      else if (healthFilter === "nodata") rows = rows.filter((r) => r.health === "pending");
    }
    if (search.trim()) {
      const q = search.toLowerCase();
      rows = rows.filter((r) => r.clientName.toLowerCase().includes(q));
    }
    return rows;
  }, [summaries, practiceFilter, healthFilter, search]);

  // Period options — last 7 months
  const periodOptions = useMemo(() => {
    const opts: { value: string; label: string }[] = [];
    const now = new Date();
    for (let i = 1; i <= 7; i++) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const val = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
      const label = d.toLocaleDateString("en-US", { month: "long", year: "numeric" });
      opts.push({ value: val, label });
    }
    return opts;
  }, []);

  const periodLabel = period
    ? new Date(period + "-01").toLocaleDateString("en-US", { month: "long", year: "numeric" })
    : "";

  return (
    <DashboardLayout>
      <div className="flex flex-col min-h-screen" style={{ background: C.pageBg }}>

        {/* Page Header */}
        <div
          className="px-8 py-5 flex items-start justify-between gap-4 flex-wrap"
          style={{
            background: C.cardBg,
            borderBottom: `1px solid ${C.headerBorder}`,
            boxShadow: "0 1px 4px rgba(10,20,40,0.06)",
          }}
        >
          <div>
            <h1
              className="text-2xl font-bold tracking-tight"
              style={{ fontFamily: "'Playfair Display', serif", color: C.textPrimary }}
            >
              Client Analytics
            </h1>
            <p className="text-sm mt-0.5" style={{ color: C.textMuted }}>
              Financial health, anomalies, and audit trail for all CORE clients
            </p>
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            {/* Period selector */}
            <select
              value={period}
              onChange={(e) => setPeriod(e.target.value)}
              className="h-9 px-3 text-sm rounded-lg outline-none"
              style={{
                background: C.cardBg,
                border: `1px solid ${C.cardBorder}`,
                color: C.textPrimary,
                minWidth: 160,
              }}
            >
              {periodOptions.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
            <button
                className="h-9 px-4 rounded-lg text-sm font-semibold flex items-center gap-2 transition-all relative"
                style={{
                  background: openFlagCount > 0 ? C.redBg : C.cardBg,
                  border: `1px solid ${openFlagCount > 0 ? C.redBorder : C.cardBorder}`,
                  color: openFlagCount > 0 ? C.red : C.textSecond,
                  boxShadow: C.cardShadow,
                }}
                onClick={() => setFlagReviewOpen(true)}
                title="View and manage data integrity flags"
              >
                <ShieldAlert className="w-4 h-4" />
                Data Flags
                {openFlagCount > 0 && (
                  <span className="text-xs font-bold px-1.5 py-0.5 rounded-full" style={{ background: C.red, color: "#fff" }}>
                    {openFlagCount}
                  </span>
                )}
              </button>
            {isAdmin && (
              <button
                className="h-9 px-4 rounded-lg text-sm font-semibold flex items-center gap-2 transition-all"
                style={{
                  background: exportLoading ? C.tableHead : C.cardBg,
                  border: `1px solid ${C.cardBorder}`,
                  color: C.textSecond,
                  boxShadow: C.cardShadow,
                }}
                onClick={handleExportReconciliation}
                disabled={exportLoading}
                title="Export Xero vs Hub client reconciliation as CSV (admin only)"
                onMouseEnter={(e) => { e.currentTarget.style.background = C.tableHead; }}
                onMouseLeave={(e) => { if (!exportLoading) e.currentTarget.style.background = C.cardBg; }}
              >
                {exportLoading ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Download className="w-4 h-4" />
                )}
                Export Reconciliation
              </button>
            )}
            {isManager && (
              <button
                className="h-9 px-5 rounded-lg text-sm font-semibold flex items-center gap-2 transition-all"
                style={{
                  background: C.cardBg,
                  border: `1px solid ${C.cardBorder}`,
                  color: C.textSecond,
                  boxShadow: C.cardShadow,
                }}
                onClick={() => pullAllMutation.mutate({ period })}
                disabled={pullAllMutation.isPending}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = C.tableHead;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = C.cardBg;
                }}
              >
                {pullAllMutation.isPending ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <RefreshCw className="w-4 h-4" />
                )}
                Pull All Clients
              </button>
            )}
          </div>
        </div>

        {/* KPI Strip — 3 client-count chips + 3 health cards */}
        <div className="px-8 pt-5 pb-2 flex items-center gap-3 flex-wrap">
          {/* Total / Xero / Non-Xero chips */}
          <div
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-semibold"
            style={{ background: C.goldLight, border: `1px solid ${C.goldBorder}`, color: C.gold }}
          >
            <span style={{ fontSize: "1.05rem", fontWeight: 700 }}>{clientCounts?.total ?? "—"}</span>
            <span style={{ color: C.amber, fontWeight: 500, fontSize: "0.78rem" }}>Total Active Clients</span>
          </div>
          <div
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm"
            style={{ background: C.cardBg, border: `1px solid ${C.cardBorder}`, color: C.textSecond }}
          >
            <CheckCircle className="w-3.5 h-3.5" style={{ color: C.green }} />
            <span style={{ fontWeight: 700 }}>{clientCounts?.xeroCount ?? "—"}</span>
            <span style={{ fontSize: "0.78rem" }}>Xero Connected</span>
          </div>
          <div
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm"
            style={{ background: C.cardBg, border: `1px solid ${C.cardBorder}`, color: C.textSecond }}
          >
            <BarChart3 className="w-3.5 h-3.5" style={{ color: C.textMuted }} />
            <span style={{ fontWeight: 700 }}>{clientCounts?.nonXeroCount ?? "—"}</span>
            <span style={{ fontSize: "0.78rem" }}>Non-Xero</span>
          </div>
        </div>
        {/* Health KPI strip */}
        <div className="px-8 pt-3 pb-4 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
          <KpiCard label="Total Clients" value={stats.total} />
          <KpiCard label="With Data" value={stats.withData} color={C.blue} />
          <KpiCard label="Healthy" value={stats.healthy} color={C.green} />
          <KpiCard label="Review" value={stats.review} color={C.amber} />
          <KpiCard label="Alert" value={stats.alert} color={C.red} />
        </div>

        {/* Filters + Table */}
        <div className="px-8 pb-8 flex-1">
          <div
            className="rounded-xl overflow-hidden"
            style={{
              background: C.cardBg,
              border: `1px solid ${C.cardBorder}`,
              boxShadow: C.cardShadow,
            }}
          >
            {/* Filter bar */}
            <div
              className="px-5 py-3 flex items-center justify-between gap-3 flex-wrap"
              style={{ borderBottom: `1px solid ${C.tableDiv}` }}
            >
              <div className="flex items-center gap-2 flex-wrap">
                {/* Search */}
                <div className="relative">
                  <Search
                    className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5"
                    style={{ color: C.textFaint }}
                  />
                  <input
                    placeholder="Search clients..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="pl-8 h-8 w-52 text-sm rounded-md outline-none"
                    style={{
                      background: C.tableHead,
                      border: `1px solid ${C.cardBorder}`,
                      color: C.textPrimary,
                    }}
                    onFocus={(e) => (e.currentTarget.style.borderColor = C.goldBorder)}
                    onBlur={(e) => (e.currentTarget.style.borderColor = C.cardBorder)}
                  />
                </div>

                {/* Practice filter */}
                <select
                  value={practiceFilter}
                  onChange={(e) => setPracticeFilter(e.target.value)}
                  className="h-8 px-2 text-sm rounded-md outline-none"
                  style={{
                    background: C.tableHead,
                    border: `1px solid ${C.cardBorder}`,
                    color: C.textSecond,
                  }}
                >
                  <option value="all">All Practices</option>
                  <option value="apx">APX</option>
                  <option value="apx-hrb">APX-HRB</option>
                  <option value="apx-bbn">APX-BBN</option>
                </select>

                {/* Health filter */}
                <select
                  value={healthFilter}
                  onChange={(e) => setHealthFilter(e.target.value)}
                  className="h-8 px-2 text-sm rounded-md outline-none"
                  style={{
                    background: C.tableHead,
                    border: `1px solid ${C.cardBorder}`,
                    color: C.textSecond,
                  }}
                >
                  <option value="all">All Health</option>
                  <option value="healthy">Healthy</option>
                  <option value="review">Review</option>
                  <option value="alert">Alert</option>
                  <option value="nodata">No Data</option>
                </select>

                {/* Client type filter */}
                <select
                  value={typeFilter}
                  onChange={(e) => setTypeFilter(e.target.value)}
                  className="h-8 px-2 text-sm rounded-md outline-none"
                  style={{
                    background: typeFilter !== "all" ? C.goldLight : C.tableHead,
                    border: `1px solid ${typeFilter !== "all" ? C.goldBorder : C.cardBorder}`,
                    color: typeFilter !== "all" ? C.amber : C.textSecond,
                    fontWeight: typeFilter !== "all" ? 600 : 400,
                  }}
                >
                  <option value="all">All Types</option>
                  <option value="xero">Xero Only</option>
                  <option value="non_xero">Non-Xero Only</option>
                </select>
              </div>

              <span className="text-sm" style={{ color: C.textMuted }}>
                {filtered.length} clients
              </span>
            </div>

            {/* Table */}
            {isLoading ? (
              <div
                className="flex items-center justify-center h-48 gap-2"
                style={{ color: C.textMuted }}
              >
                <Loader2 className="w-5 h-5 animate-spin" />
                <span className="text-sm">Loading client data...</span>
              </div>
            ) : filtered.length === 0 ? (
              <div
                className="flex flex-col items-center justify-center h-48 gap-2"
                style={{ color: C.textMuted }}
              >
                <BarChart3 className="w-8 h-8 opacity-30" />
                <p className="text-sm">No clients match the current filters</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr
                      style={{
                        background: C.tableHead,
                        borderBottom: `2px solid ${C.tableDiv}`,
                        position: "sticky",
                        top: 0,
                        zIndex: 10,
                      }}
                    >
                      {[
                        { label: "CLIENT",       align: "left",   w: "22%" },
                        { label: "PRACTICE",     align: "left",   w: "10%" },
                        { label: "INCOME",       align: "right",  w: "11%" },
                        { label: "EXPENSES",     align: "right",  w: "11%" },
                        { label: "NET PROFIT",   align: "right",  w: "11%" },
                        { label: "ANOMALIES",    align: "center", w: "9%"  },
                        { label: "HEALTH",       align: "center", w: "11%" },
                        { label: "LAST PULLED",  align: "left",   w: "11%" },
                        { label: "",             align: "right",  w: "4%"  },
                      ].map((h, i) => (
                        <th
                          key={i}
                          className="px-4 py-2.5 text-xs font-bold uppercase tracking-wider"
                          style={{
                            color: C.textMuted,
                            width: h.w,
                            textAlign: h.align as React.CSSProperties["textAlign"],
                          }}
                        >
                          {h.label}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map((row) => {
                      const isPending = row.health === "pending";
                      const isPulling =
                        pullClientMutation.isPending &&
                        pullClientMutation.variables?.clientId === row.clientId;

                      return (
                        <tr
                          key={row.clientId}
                          style={{ borderBottom: `1px solid ${C.tableDiv}` }}
                          onMouseEnter={(e) =>
                            (e.currentTarget.style.background = C.tableRowHov)
                          }
                          onMouseLeave={(e) =>
                            (e.currentTarget.style.background = "transparent")
                          }
                        >
                          {/* Client */}
                          <td className="px-4 py-3">
                            <p
                              className="font-semibold leading-tight"
                              style={{ color: C.textPrimary }}
                            >
                              {row.clientName}
                            </p>
                          </td>

                          {/* Practice */}
                          <td className="px-4 py-3">
                            <span style={practiceBadgeStyle(row.practiceKey)}>
                              {practiceLabel(row.practiceKey)}
                            </span>
                          </td>

                          {/* Income */}
                          <td className="px-4 py-3 text-right">
                            {isPending ? (
                              <span style={{ color: C.textFaint }}>$0</span>
                            ) : (
                              <span style={{ color: C.textSecond }}>
                                {fmtCurrency(row.totalIncome)}
                              </span>
                            )}
                          </td>

                          {/* Expenses */}
                          <td className="px-4 py-3 text-right">
                            {isPending ? (
                              <span style={{ color: C.textFaint }}>$0</span>
                            ) : (
                              <span style={{ color: C.red }}>
                                ({fmtCurrency(Math.abs(row.totalExpenses))})
                              </span>
                            )}
                          </td>

                          {/* Net Profit */}
                          <td className="px-4 py-3 text-right">
                            {isPending ? (
                              <span style={{ color: C.textFaint }}>$0</span>
                            ) : row.netProfit < 0 ? (
                              <span
                                className="inline-flex items-center gap-0.5 font-semibold"
                                style={{ color: C.red }}
                              >
                                <TrendingDown className="w-3 h-3" />
                                {fmtCurrency(row.netProfit)}
                              </span>
                            ) : row.netProfit > 0 ? (
                              <span
                                className="inline-flex items-center gap-0.5 font-medium"
                                style={{ color: C.green }}
                              >
                                <TrendingUp className="w-3 h-3" />
                                {fmtCurrency(row.netProfit)}
                              </span>
                            ) : (
                              <span style={{ color: C.textFaint }}>$0</span>
                            )}
                          </td>

                          {/* Anomalies */}
                          <td className="px-4 py-3 text-center">
                            {row.anomalyCount > 0 ? (
                              <span
                                className="inline-flex items-center justify-center w-6 h-6 rounded-full text-xs font-bold"
                                style={
                                  row.health === "red"
                                    ? {
                                        background: C.redBg,
                                        color: C.red,
                                        border: `1px solid ${C.redBorder}`,
                                      }
                                    : {
                                        background: C.amberBg,
                                        color: C.amber,
                                        border: `1px solid ${C.amberBorder}`,
                                      }
                                }
                              >
                                {row.anomalyCount}
                              </span>
                            ) : isPending ? (
                              <span style={{ color: C.textFaint }}>—</span>
                            ) : (
                              <span style={{ color: C.textFaint }}>—</span>
                            )}
                          </td>

                          {/* Health */}
                          <td className="px-4 py-3 text-center">
                            <HealthBadge health={row.health} />
                          </td>

                          {/* Last Pulled */}
                          <td className="px-4 py-3">
                            <span
                              className="text-xs flex items-center gap-1"
                              style={{ color: isPending ? C.amber : C.textFaint }}
                            >
                              {isPending ? (
                                <span className="font-semibold" style={{ color: C.amber }}>
                                  Never pulled
                                </span>
                              ) : (
                                <>
                                  <Clock className="w-3 h-3 shrink-0" />
                                  {fmtDate(row.lastPulled)}
                                </>
                              )}
                            </span>
                          </td>

                          {/* Actions */}
                          <td className="px-4 py-3">
                            <div className="flex items-center justify-end gap-1">
                              {/* Re-pull icon */}
                              <button
                                title="Re-pull data from Xero"
                                className="w-7 h-7 flex items-center justify-center rounded-md transition-all"
                                style={{
                                  background: "transparent",
                                  border: `1px solid ${C.cardBorder}`,
                                  color: C.textFaint,
                                }}
                                onMouseEnter={(e) => {
                                  e.currentTarget.style.background = C.blueBg;
                                  e.currentTarget.style.color = C.blue;
                                  e.currentTarget.style.borderColor = C.blueBorder;
                                }}
                                onMouseLeave={(e) => {
                                  e.currentTarget.style.background = "transparent";
                                  e.currentTarget.style.color = C.textFaint;
                                  e.currentTarget.style.borderColor = C.cardBorder;
                                }}
                                onClick={() =>
                                  pullClientMutation.mutate({
                                    clientId: row.clientId,
                                    period,
                                  })
                                }
                                disabled={isPulling}
                              >
                                {isPulling ? (
                                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                ) : (
                                  <RotateCcw className="w-3.5 h-3.5" />
                                )}
                              </button>

                              {/* View → */}
                              <button
                                title="View in Xero|Manus Active"
                                className="flex items-center gap-1 px-2 h-7 rounded-md text-xs font-semibold transition-all"
                                style={{
                                  background: "transparent",
                                  border: `1px solid ${C.cardBorder}`,
                                  color: C.textMuted,
                                }}
                                onMouseEnter={(e) => {
                                  e.currentTarget.style.background = C.tableHead;
                                  e.currentTarget.style.color = C.textSecond;
                                }}
                                onMouseLeave={(e) => {
                                  e.currentTarget.style.background = "transparent";
                                  e.currentTarget.style.color = C.textMuted;
                                }}
                                onClick={() => {
                                  window.location.href = "/client-analytics";
                                }}
                              >
                                View <ArrowRight className="w-3 h-3" />
                              </button>
                              {/* Flag button */}
                              <button
                                title="Flag suspicious data"
                                className="w-7 h-7 flex items-center justify-center rounded-md transition-all"
                                style={{
                                  background: "transparent",
                                  border: `1px solid ${C.cardBorder}`,
                                  color: C.textFaint,
                                }}
                                onMouseEnter={(e) => {
                                  e.currentTarget.style.background = C.amberBg;
                                  e.currentTarget.style.color = C.amber;
                                  e.currentTarget.style.borderColor = C.amberBorder;
                                }}
                                onMouseLeave={(e) => {
                                  e.currentTarget.style.background = "transparent";
                                  e.currentTarget.style.color = C.textFaint;
                                  e.currentTarget.style.borderColor = C.cardBorder;
                                }}
                                onClick={() => setFlagModal({
                                  clientId: row.clientId,
                                  clientName: row.clientName,
                                  income: row.totalIncome,
                                  expenses: row.totalExpenses,
                                  netProfit: row.netProfit,
                                })}
                              >
                                <Flag className="w-3.5 h-3.5" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>
      {/* Flag Data Modal */}
      {flagModal && (
        <FlagDataModal
          open={!!flagModal}
          onClose={() => setFlagModal(null)}
          clientId={flagModal.clientId}
          clientName={flagModal.clientName}
          period={period}
          income={flagModal.income}
          expenses={flagModal.expenses}
          netProfit={flagModal.netProfit}
          flaggedBy={user?.name ?? user?.email ?? "Staff"}
          flaggedByRole={user?.role ?? "staff"}
          onSuccess={() => refetchFlagCount()}
        />
      )}
      {/* Flag Review Panel */}
      <FlagReviewPanel
        open={flagReviewOpen}
        onClose={() => { setFlagReviewOpen(false); refetchFlagCount(); }}
        period={period}
        resolvedBy={user?.name ?? user?.email ?? "Admin"}
      />
    </DashboardLayout>
  );
}
